import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../config';

// "MM:SS" residuo verso untilIso; '' se scaduto/assente (= test in corso).
export function formatRemaining(untilIso, nowMs = Date.now()) {
  if (!untilIso) return '';
  const diff = new Date(untilIso).getTime() - nowMs;
  if (isNaN(diff) || diff <= 0) return '';
  const tot = Math.floor(diff / 1000);
  const m = Math.floor(tot / 60), s = tot % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

// Countdown che si aggiorna ogni secondo. render=(testo) -> JSX.
export function Remaining({ until, render }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  return render(formatRemaining(until, now));
}

// SVG statico "segnale interrotto" (no emoji): barre segnale con sbarra diagonale.
function NoSignalSvg({ size = 40 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="3" y="14" width="3" height="6" rx="1" fill="#f59e0b" />
      <rect x="8.5" y="10" width="3" height="10" rx="1" fill="#f59e0b" opacity="0.5" />
      <rect x="14" y="6" width="3" height="14" rx="1" fill="#f59e0b" opacity="0.3" />
      <line x1="4" y1="20" x2="20" y2="5" stroke="#b45309" strokeWidth="2.2" strokeLinecap="round" />
    </svg>
  );
}

// Icona nella sidebar (sostituisce l'animazione durante lo standby da disservizio).
export function DisservizioIcon({ standbyUntil, collapsed, onClick }) {
  return (
    <div className="agent-animation" onClick={onClick} style={{ cursor: 'pointer', textAlign: 'center' }}
      title="Disservizio 456: agente in standby. Clicca per i dettagli.">
      <div className="agent-anim-wrap" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <NoSignalSvg size={collapsed ? 26 : 40} />
      </div>
      {!collapsed && (
        <div style={{ fontSize: 11, color: '#b45309', fontWeight: 700, marginTop: 2 }}>
          <Remaining until={standbyUntil} render={(t) => (t ? `Riprova tra ${t}` : 'Verifica in corso…')} />
        </div>
      )}
    </div>
  );
}

const TIPO_LABEL = {
  outage_rilevato: (d) => `Disservizio rilevato (test: ${d.risultato_test || '—'})`,
  tentativo_fallito: (d) => `Ritentativo fallito (test: ${d.risultato_test || '—'})${d.durata_minuti ? ` — riprova tra ${d.durata_minuti} min` : ''}`,
  ripresa: () => 'Servizio ripristinato',
};
const TIPO_COLOR = { outage_rilevato: '#b91c1c', tentativo_fallito: '#b45309', ripresa: '#16a34a' };
const ora = (iso) => { try { return new Date(iso).toLocaleString('it-IT'); } catch { return iso || '—'; } };

export default function DisservizioDialog({ open, onClose, standbyUntil }) {
  const [storia, setStoria] = useState([]);
  const [busy, setBusy] = useState(false);

  const loadStoria = useCallback(() => {
    axios.get(`${API}/settings/health-log`, { params: { limit: 100 } })
      .then(r => setStoria((r.data?.items || []).filter(d => TIPO_LABEL[d.tipo])))
      .catch(() => {});
  }, []);
  useEffect(() => { if (open) loadStoria(); }, [open, loadStoria]);

  const provaOra = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/agent/disservizio/prova-ora`);
      toast.success('Test forzato: parte entro pochi secondi');
      setTimeout(loadStoria, 1500);
    } catch (e) {
      toast.info(e.response?.data?.detail || 'Nessun disservizio in corso');
    } finally { setBusy(false); }
  };

  if (!open) return null;
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: 'var(--surface)', borderRadius: 14,
        width: 420, maxWidth: '92vw', padding: 20, boxShadow: '0 10px 40px rgba(0,0,0,.3)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
          <h3 style={{ margin: 0, fontSize: 16 }}>Disservizio 456 — agente in standby</h3>
          <button className="btn btn-sm" onClick={onClose}>✕</button>
        </div>
        <p style={{ margin: '0 0 12px', color: '#64748b', fontSize: 13 }}>
          Il servizio 456 segnala problemi tecnici. L'agente è in pausa e riprova periodicamente.
        </p>

        <div style={{ textAlign: 'center', margin: '8px 0 16px' }}>
          <div style={{ fontSize: 12, color: '#64748b' }}>Prossimo tentativo tra</div>
          <div style={{ fontSize: 34, fontWeight: 800, color: '#0c2f53', fontVariantNumeric: 'tabular-nums' }}>
            <Remaining until={standbyUntil} render={(t) => (t || 'in corso…')} />
          </div>
        </div>

        <div style={{ fontSize: 12, color: '#64748b', marginBottom: 6 }}>Cronologia</div>
        <div style={{ maxHeight: 240, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 10, padding: 8 }}>
          {storia.length === 0 ? (
            <div style={{ color: '#94a3b8', fontSize: 13, padding: 6 }}>Nessun evento registrato.</div>
          ) : storia.map((d) => (
            <div key={d.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 10,
              fontSize: 12.5, padding: '5px 4px', borderBottom: '1px solid #f1f5f9' }}>
              <span style={{ color: TIPO_COLOR[d.tipo] || '#475569', fontWeight: 600 }}>{TIPO_LABEL[d.tipo](d)}</span>
              <span style={{ color: '#94a3b8', whiteSpace: 'nowrap' }}>{ora(d.timestamp)}</span>
            </div>
          ))}
        </div>

        <div style={{ marginTop: 14, textAlign: 'right' }}>
          <button className="btn btn-primary" disabled={busy} onClick={provaOra}>
            {busy ? 'Invio…' : 'Prova immediatamente'}
          </button>
        </div>
      </div>
    </div>
  );
}
